import React, { useEffect, useRef, useState } from 'react';
import { Icon, IconButton, Snackbar, Tooltip } from '@mui/material';

import ProgressBar from '../controls/ProgressBar';

import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';
import { planManagementService } from '../../services/plan-management.service';
import DialogBeoordelingAdd from '../controls/dialogs/DialogBeoordelingAdd';
import DialogBeoordelingTimeline from '../controls/dialogs/DialogBeoordelingTimeline';
import { authService } from '../../services/auth.service';
import { contentService } from '../../services/content.service';
import { userManagementService } from '../../services/user-management.service';

interface PlanBeoordelingenProps {
  infplId : string,
  planNaam?: string;
  beoordelingen?: any[];
  onSubmitBeoordeling?: () => void;
}

const PlanBeoordelingen: React.FC<PlanBeoordelingenProps> = ({
  infplId,
  planNaam,
  beoordelingen,
  onSubmitBeoordeling
}) => {
  const [mijnOrganisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem | null>(null);
  const canCrud = authService.canCrud();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const [currentBeoordelingen, setCurrentBeoordelingen] = useState<any[]>([]);
  const [timelineOrganisatie, setTimelineOrganisatie] = useState<string | null>(null);
  const [timelineBeoordelingen, setTimelineBeoordelingen] = useState<any[]>([]);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    timeline: false,
    add: false,
    snackbar: false
  });

  // Ref to prevent fetching data twice in Strict Mode
  const hasFetchedData = useRef(false);

  useEffect(() => {
    getStoredOrganisatie();
  }, []);

  useEffect(() => {
    const getSortedBeoordelingen = () => {
      if (!beoordelingen || !Array.isArray(beoordelingen)) return [];
      const idToCompare = mijnOrganisatie?.id;
  
      // Clone to avoid mutating original
      return [...beoordelingen].sort((a, b) => {
        // Put "mijnOrganisatie" first
        if (idToCompare) {
          const isA_OwnOrganisation = a.planBeoordelingOrganizationId === idToCompare;
          const isB_OwnOrganisation = b.planBeoordelingOrganizationId === idToCompare;
  
          if (isA_OwnOrganisation && !isB_OwnOrganisation) return -1;
          if (!isA_OwnOrganisation && isB_OwnOrganisation) return 1;
        }
  
        // Then: sort alphabetically
        return a.planBeoordelingOrganizationName.localeCompare(
          b.planBeoordelingOrganizationName,
          'nl', // Optional: enforce Dutch alphabetical sorting
          { sensitivity: 'base' } // Optional: case-insensitive, accent-insensitive
        );
      });
    };
  
    const sorted = getSortedBeoordelingen();
    setCurrentBeoordelingen(sorted);

  }, [infplId, beoordelingen, mijnOrganisatie]);

  const getStoredOrganisatie = async () => {
    const organisatie = userManagementService.getStoredOrganisatie();
    if (organisatie) {
        setMijnOrganisatie(organisatie);
    }
  };

  const fetchBeoordelingenCurrent = async () => {
    setLoading(true);
    try {
      const result = await planManagementService.getPlanBeoordelingenCurrent(infplId) as any;

      if (result && result.planBeoordelingen) {
        const beoordelingen = result.planBeoordelingen;
        if (beoordelingen) {
          setCurrentBeoordelingen(beoordelingen);
        }
      }
      setLoading(false);
    }
    catch (error: any) {
      setLoading(false);
      console.error('Error fetching beoordelingen:', error);
      setError(error.message || 'Er is een fout opgetreden');
    }
  }

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const onClickOpenDialogBeoordelingAdd = (open: boolean = false) => {
    openDialog('add');
  };

  const onClickOpenDialogBeoordelingHistory = async (orgId: string, organisatieNaam: string, open: boolean = false) => {
    setLoading(true);
    try {
      const result = await planManagementService.getPlanBeoordelingenHistory(infplId, orgId) as any;

      if (result && result.planBeoordelingen) {
        const beoordelingen = result.planBeoordelingen;

        if (beoordelingen && beoordelingen.length === 0) {
          setSnackbarMessage('Er zijn geen beoordelingen gevonden voor deze organisatie');
          openDialog('snackbar');
          setLoading(false);
          return;
        }

        setTimelineOrganisatie(organisatieNaam);
        setTimelineBeoordelingen(beoordelingen);
        openDialog('timeline');
      }
      
      setLoading(false);
    }
    catch (error: any) {
      setLoading(false);
      console.error('Error fetching beoordelingen history:', error);
      openDialog('snackbar')
      // setError(error.message || 'Er is een fout opgetreden bij het ophalen van de beoordelingshistorie');
    }
  };

  const handleSubmitBeoordeling = async (beoordeling: any) => {
    try {
      setLoading(true);
      const response = await planManagementService.addPlanBeoordeling(beoordeling, infplId);
      if (response.ok) {
        closeDialog('add');

        if (onSubmitBeoordeling) {
          onSubmitBeoordeling();
        }
        // fetchBeoordelingenCurrent();
      }
      setLoading(false);
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating beoordeling:', error);
      setError('Er is een fout opgetreden');
    }
  };

  const getOrganisatieNaam = (id: string) => {
    let naam = id;
    return naam; // planBeoordelingOrganizationId
  };

  const getBeoordelingTooltip = (key: string) => {
    let tooltipText = contentService.getBeoordelingStatusDescription(key);
    return tooltipText;
  };

  const getBeoordelingIcon = (key: string) => {
    let icon = contentService.getBeoordelingStatusIcon(key);
    return icon;
  };

  const getBeoordelingStatusLabel = (key: string) => {
    const label = contentService.getBeoordelingStatusLabel(key);
    return label;
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      <div className="beoordeling-grid">
        {currentBeoordelingen.length > 0 ? (
          currentBeoordelingen.map((item: any, index: number) => (
            <div 
            className={`beoordeling-card  ${(mijnOrganisatie && item.planBeoordelingOrganizationId === mijnOrganisatie.id) ? `mijn_organisatie ${item.key.id}` : ''}`}
              key={index}>
              <div className="beoordeling-header">
                <Tooltip title={getBeoordelingTooltip(item.key.id)} placement="right" arrow>
                  <span className={`beoordeling-icon ${item.key.id}`}>
                    <span className="material-icons">{getBeoordelingIcon(item.key.id)}</span>
                  </span>
                </Tooltip>

                <div className="beoordeling-status">
                  <strong>Status:</strong> {getBeoordelingStatusLabel(item.key.id)}
                </div>
              </div>

              {item.remark && (
                <div className="beoordeling-remark">
                  <p>{item.remark}</p>
                </div>
              )}

              <div className="beoordeling-footer">
                <div className="beoordeling-meta">
                  <div className="meta-left">
                    <div className="organisation">{item.planBeoordelingOrganizationName}</div>
                  </div>

                  {item.key.id !== 'nog_niet_beoordeeld' && (
                    <div className="meta-right">
                      <div className="date">
                        {new Date(item.timestamp).toLocaleDateString('nl-NL', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                      <div className="user">{item.user.toLowerCase()}</div>
                    </div>
                  )}
                </div>

                <div className="beoordeling-actions">
                  <Tooltip title="Bekijk beoordeling historie" arrow>
                    <IconButton color="secondary" size="small" onClick={() => onClickOpenDialogBeoordelingHistory(item.planBeoordelingOrganizationId, item.planBeoordelingOrganizationName)}>
                      <Icon>timeline</Icon>
                    </IconButton>
                  </Tooltip>

                  {mijnOrganisatie && item.planBeoordelingOrganizationId === mijnOrganisatie.id && canCrud && (
                    <Tooltip title={item.key.id !== 'nog_niet_beoordeeld' ? 'Beoordeling wijzigen' : 'Beoordelen'} arrow>
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={() => onClickOpenDialogBeoordelingAdd(true)}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>Geen beoordelingen</p>
        )}
      </div>

      <DialogBeoordelingTimeline
        open={open.timeline}
        onClose={ () => closeDialog('timeline') }
        organisatie={timelineOrganisatie || ''}
        beoordelingen={timelineBeoordelingen}/>

      <DialogBeoordelingAdd
        open={open.add}
        planNaam={planNaam ? planNaam : ''}
        onClose={ () => closeDialog('add') }
        onSubmit={handleSubmitBeoordeling}/>

      { loading && (
        <ProgressBar />
      )}

      {/* <code style={{ fontSize: '11px', top: '60vh', position: 'relative' }}>
        {data.features.map((feature: any, index: number) => (
          <div key={index}>
            <strong>Feature {index + 1}</strong> <strong>Type:</strong> {feature.geometry.type}
            <br />
            <strong>Coordinates:</strong> {JSON.stringify(feature.geometry.coordinates)}
            <br />
            <strong>Properties:</strong> {JSON.stringify(feature.properties)}
          </div>
        ))}
      </code> */}
    </>
  );
};

export default PlanBeoordelingen;